import { Folder, FolderContent } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { Ellipsis } from '@/components/Ellipsis';
import { useFolderHierarchy } from '@/hooks/api/folders/useFolderHierarchy';
import { getContentParentFolderId, isFolder } from '@/utils/folders';
import HomeIcon from '@mui/icons-material/Home';
import { MoreHoriz } from '@mui/icons-material';
import { Box, Breadcrumbs, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDroppable } from '@dnd-kit/core';
import { useContentDragStateOptional } from './ContentDragContext';

type FolderBreadcrumb = Pick<Folder, 'id' | 'name' | 'parentFolderId'>;

const MAX_VISIBLE_FOLDERS = 4;

const HiddenFoldersIconButton = styled(IconButton)(({ theme }) => ({
  width: '26px',
  height: '26px',
  border: '1px dashed',
  borderColor: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  flexShrink: 0,
  '& svg': {
    fontSize: '18px',
  },
}));

const dropZoneSx = {
  display: 'flex',
  alignItems: 'center',
  paddingX: 1.5,
  paddingY: 0.5,
  marginRight: 0.5,
  borderRadius: '6px',
  border: '1px dashed',
  borderColor: 'text.secondary',
  transition: 'all 0.15s ease-in-out',
};

const DroppableBreadcrumbItem = ({
  folderId,
  label,
  isRoot,
  draggedContents,
}: {
  folderId: string | undefined;
  label: string;
  isRoot?: boolean;
  draggedContents: FolderContent[];
}) => {
  const dragContext = useContentDragStateOptional();
  const dropTargetFolderId = dragContext?.dropTargetFolderId;

  const allContentAlreadyHere = draggedContents.every(content => {
    const parentId = getContentParentFolderId(content);
    if (isRoot) {
      return parentId === undefined;
    }
    return parentId === folderId;
  });

  const isDraggingThisFolder = draggedContents.some(content => isFolder(content) && content.id === folderId);

  const isDisabled = allContentAlreadyHere || isDraggingThisFolder;

  const { setNodeRef, isOver } = useDroppable({
    id: isRoot ? 'root-drop-zone' : `breadcrumb-drop-${folderId}`,
    data: isRoot ? { type: 'root-drop-target' } : { type: 'folder-drop-target', folderId, folderName: label },
    disabled: isDisabled,
  });

  const isDropTarget = isOver || (isRoot ? dropTargetFolderId === 'root' : dropTargetFolderId === folderId);
  const showActiveState = isDropTarget && !isDisabled;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        ...dropZoneSx,
        cursor: isDisabled ? 'not-allowed' : 'default',
        opacity: isDisabled ? 0.5 : 1,
        ...(showActiveState && {
          backgroundColor: 'primary.main',
          borderColor: 'primary.main',
          borderStyle: 'solid',
          color: 'primary.contrastText',
          '& .MuiSvgIcon-root': {
            color: 'primary.contrastText',
          },
        }),
      }}
    >
      {isRoot ? (
        <HomeIcon sx={{ fontSize: 18 }} />
      ) : (
        <BodyText sx={{ minWidth: 0 }}>
          <Ellipsis text={label} tooltipProps={{ placement: 'bottom' }} />
        </BodyText>
      )}
    </Box>
  );
};

const HiddenFoldersDropZone = ({ hiddenFolders }: { hiddenFolders: FolderBreadcrumb[] }) => {
  const dragContext = useContentDragStateOptional();
  const dropTargetFolderId = dragContext?.dropTargetFolderId;

  const { setNodeRef, isOver } = useDroppable({
    id: 'hidden-folders-drop-zone',
    data: { type: 'hidden-folders-drop-target' },
  });

  const isDropTarget = isOver || dropTargetFolderId === 'hidden-folders';
  const hiddenPathTooltip = hiddenFolders.map(f => f.name).join(' / ');

  return (
    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }} data-hidden-folders-button data-no-shrink>
      <Tooltip title={`Drop to choose: ${hiddenPathTooltip}`} placement='bottom'>
        <HiddenFoldersIconButton
          ref={setNodeRef}
          sx={{
            ...(isDropTarget && {
              backgroundColor: 'primary.main',
              borderColor: 'primary.main',
              borderStyle: 'solid',
              color: 'primary.contrastText',
            }),
          }}
        >
          <MoreHoriz />
        </HiddenFoldersIconButton>
      </Tooltip>
    </Box>
  );
};

export const DroppableBreadcrumbs = ({ folderId }: { folderId?: string }) => {
  const { hierarchy } = useFolderHierarchy({ folderId });
  const dragContext = useContentDragStateOptional();
  const draggedContents = dragContext?.draggedContents ?? [];

  const displayHierarchy: FolderBreadcrumb[] =
    hierarchy?.map(({ id, name, parentFolderId }) => ({
      id,
      name,
      parentFolderId,
    })) ?? [];

  const visibleFolders = displayHierarchy.slice(-MAX_VISIBLE_FOLDERS);
  const hiddenFolders = displayHierarchy.slice(0, -MAX_VISIBLE_FOLDERS);
  const hasHiddenFolders = hiddenFolders.length > 0;

  const ancestorFolders = visibleFolders.slice(0, -1);
  const currentFolder = visibleFolders.at(-1);

  return (
    <Breadcrumbs
      aria-label='drop zone breadcrumbs'
      sx={{
        color: 'text.primary',
        flexWrap: 'nowrap',
        minWidth: 0,
        '& .MuiBreadcrumbs-ol': {
          flexWrap: 'nowrap',
        },
        '& .MuiBreadcrumbs-li': {
          overflow: 'hidden',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 1,
        },
        '& .MuiBreadcrumbs-li:has([data-no-shrink])': {
          flexShrink: 0,
          minWidth: 'fit-content',
        },
        '& .MuiBreadcrumbs-separator': {
          mx: 1,
        },
      }}
    >
      <Box data-no-shrink sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        <DroppableBreadcrumbItem folderId={undefined} label='Assets' isRoot draggedContents={draggedContents} />
      </Box>
      {hasHiddenFolders && <HiddenFoldersDropZone hiddenFolders={hiddenFolders} />}
      {ancestorFolders.map(folder => (
        <DroppableBreadcrumbItem
          key={folder.id}
          folderId={folder.id}
          label={folder.name}
          draggedContents={draggedContents}
        />
      ))}
      {currentFolder && (
        <Box sx={{ ...dropZoneSx, opacity: 0.5, cursor: 'not-allowed' }}>
          <BodyText sx={{ minWidth: 0 }}>
            <Ellipsis text={currentFolder.name} tooltipProps={{ placement: 'bottom' }} />
          </BodyText>
        </Box>
      )}
      <span />
    </Breadcrumbs>
  );
};
